import CtaBanner from "@/components/site/CtaBanner";
import { CheckCircle2, ArrowRight } from "lucide-react";

const STEPS = [
  {
    n: "Step 1",
    title: "Business energy review",
    body: "Share electricity bills, site details and business information so we can understand your load profile and operating pattern.",
  },
  {
    n: "Step 2",
    title: "System design",
    body: "Our team reviews your usage, roof space, load profile and battery suitability to shape a system tailored to your site.",
  },
  {
    n: "Step 3",
    title: "PPA proposal",
    body: "We prepare an indicative usage-based energy proposal — estimated system size, PPA cents-per-kWh rate and potential savings.",
  },
  {
    n: "Step 4",
    title: "Installation and commissioning",
    body: "Once you approve, we arrange installation, grid connection and commissioning through our project partners.",
  },
  {
    n: "Step 5",
    title: "Pay only for energy used",
    body: "You pay a flat cents-per-kWh rate for the energy consumed from the Be Green solar and battery system. Grid energy stays with your retailer.",
  },
  {
    n: "Step 6",
    title: "Ongoing support",
    body: "We monitor, maintain and support the system for the life of the agreement so it keeps performing.",
  },
];

export default function HowItWorks() {
  return (
    <div>
      <section className="container-x mx-auto max-w-7xl pt-14 md:pt-24 pb-10" data-testid="hiw-hero">
        <div className="max-w-3xl">
          <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-4">How our commercial energy plan works</p>
          <h1 className="font-heading text-4xl md:text-6xl font-semibold text-forest leading-[1.03] tracking-tight">
            A simple, staged process from energy review to long-term support.
          </h1>
          <p className="mt-6 text-neutral-600 text-lg leading-relaxed">
            We follow a clear six-step path so your business can evaluate the plan on the numbers,
            not the sales pitch. Every step is optional until you sign a formal PPA.
          </p>
        </div>
      </section>

      <section className="container-x mx-auto max-w-7xl pb-16 md:pb-24" data-testid="hiw-steps">
        <div className="relative">
          <div className="hidden md:block absolute left-[24px] top-8 bottom-8 w-px bg-black/10" />
          <div className="grid gap-6 md:gap-10">
            {STEPS.map((s, i) => (
              <div key={s.n} className="grid md:grid-cols-[64px_1fr] gap-5 items-start" data-testid={`hiw-step-${i}`}>
                <div className="relative">
                  <div className="h-12 w-12 rounded-2xl bg-forest text-white grid place-items-center font-heading font-semibold text-[15px]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="rounded-2xl bg-white border border-black/5 p-6 md:p-8 hover:border-forest/20 transition-colors">
                  <p className="text-bright text-[12.5px] font-semibold tracking-widest uppercase">{s.n}</p>
                  <h3 className="mt-2 font-heading text-2xl font-semibold text-forest">{s.title}</h3>
                  <p className="mt-3 text-neutral-600 text-[15.5px] leading-relaxed max-w-3xl">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-mist py-16 md:py-24">
        <div className="container-x mx-auto max-w-7xl grid md:grid-cols-3 gap-6">
          {[
            { t: "You keep your existing retailer", b: "Grid electricity remains billed by your normal retailer. We only bill for on-site solar and battery energy used." },
            { t: "System owned by Be Green Group", b: "We own, monitor and maintain the system so your team never manages the asset." },
            { t: "Indicative, not guaranteed", b: "Proposals depend on site inspection, engineering, network approval, credit approval and final agreement." },
          ].map((c, i) => (
            <div key={c.t} className="rounded-2xl bg-white p-6 border border-black/5" data-testid={`hiw-note-${i}`}>
              <CheckCircle2 size={20} className="text-bright" />
              <h4 className="mt-4 font-heading text-lg font-semibold text-forest">{c.t}</h4>
              <p className="mt-2 text-[14.5px] text-neutral-600 leading-relaxed">{c.b}</p>
            </div>
          ))}
        </div>
      </section>

      <CtaBanner
        testId="hiw-cta"
        title="Ready to see what your PPA rate could look like?"
        body="Start an online application and our team will assess your site, load and operating profile."
        ctaLabel="Start online application"
      />
    </div>
  );
}
